import moment from "moment";

// serializer
import InvoiceItemWithInvoiceImproveSerializer from "issara-sdk/types/InvoiceItemWithInvoiceImproveSerializer_apps_BIL";

import { beToAd } from "react-lib/utils/dateUtils";

import type { ChipV4Tone } from "../common/ui";

// ---------- รายการค่าใช้จ่าย ----------

/** ยอดเงินจาก API ที่อาจมาเป็น "1,000.00" */
export const amountOf = (value?: string | number | null) =>
  Number(String(value ?? "").replace(/,/g, "")) || 0;

/** แถวของ BIL/invoiceitem-with-invoice/byitem/ */
export type InvoiceItem = InvoiceItemWithInvoiceImproveSerializer;

export type PayStatus = "pending" | "package" | "paid";

export const PAY_STATUS_LABELS: Record<PayStatus, string> = {
  pending: "รอรับชำระ (Staff)",
  package: "ใช้สิทธิ์แพ็กเกจ",
  paid: "ชำระแล้ว",
};

// order_custom_status ที่ไม่แสดงในรายการ
const HIDDEN_CUSTOM_STATUS = 6;

export const payStatusOf = (item: InvoiceItem): PayStatus => {
  if (item.status_name === "PAID") {
    return "paid";
  }

  if (item.package_status === "APPLIED") {
    return "package";
  }

  return "pending";
};

export const payStatusLabel = (item: InvoiceItem) => {
  const status = payStatusOf(item);

  return status === "pending"
    ? item.order_custom_status_label || PAY_STATUS_LABELS.pending
    : PAY_STATUS_LABELS[status];
};

/** ยอดที่สิทธิรับผิดชอบ = ราคารวม − ยอดชำระ (แถวใช้สิทธิ์แพ็กเกจ = 0) */
export const coveredOf = (item: InvoiceItem) =>
  item.package_status === "APPLIED"
    ? 0
    : Math.max(0, (Number(item.price) || 0) - (Number(item.payable) || 0));

export const dueOf = (item: InvoiceItem) =>
  item.package_status === "APPLIED" ? 0 : Number(item.payable) || 0;

/** แถวที่แสดง: ตัดรายการยกเลิก และ order_custom_status ที่ซ่อน */
export const visibleItemsOf = (items: InvoiceItem[]) =>
  items.filter(
    (item) => item.status_name !== "CANCELED" && item.order_custom_status !== HIDDEN_CUSTOM_STATUS,
  );

export const encounterOptionsOf = (items: InvoiceItem[]) => {
  const numbers: string[] = [];

  for (const item of items) {
    if (item.encounter_number && !numbers.includes(item.encounter_number)) {
      numbers.push(item.encounter_number);
    }
  }

  return numbers.sort((a, b) => Number(a) - Number(b));
};

/** ยอดรวมของแถวรอรับชำระที่เลือก */
export const totalOf = (items: InvoiceItem[]) => {
  let total = 0;

  for (const item of items) {
    if (payStatusOf(item) === "pending") {
      total += dueOf(item);
    }
  }

  return total;
};

export const payButtonText = (items: InvoiceItem[]) => {
  const packageCount = items.filter((item) => payStatusOf(item) === "package").length;

  if (packageCount === 0) {
    return "รับชำระเงิน";
  }

  return packageCount === items.length ? "ยืนยันตัดสิทธิ์" : "รับชำระ / ตัดสิทธิ์";
};

// ---------- หมวด ----------

export type BillingCategory =
  "df" | "lab" | "imaging" | "treatment" | "drug" | "supply" | "misc" | "package";

export const CATEGORY_ORDER: BillingCategory[] = [
  "df",
  "lab",
  "imaging",
  "treatment",
  "drug",
  "supply",
  "misc",
  "package",
];

export const CATEGORY_LABELS: Record<BillingCategory, string> = {
  df: "Doctor Fee",
  lab: "แลป",
  imaging: "Imaging",
  treatment: "หัตถการ",
  drug: "ยา",
  supply: "เวชภัณฑ์",
  misc: "เบ็ดเตล็ด",
  package: "แพ็กเกจ",
};

// แพ็กเกจใช้ class hv4-bill-cat-package
export const CATEGORY_TONES: Record<BillingCategory, ChipV4Tone | null> = {
  df: "ok",
  lab: "purple",
  imaging: "sky",
  treatment: "orange",
  drug: "teal",
  supply: "grey",
  misc: "warn",
  package: null,
};

// core/product-type code -> หมวด
const P_TYPE_CATEGORY: Record<string, BillingCategory> = {
  DF: "df",
  LAB: "lab",
  XRAY: "imaging",
  DIAG: "imaging",
  PROC: "treatment",
  REHAB: "treatment",
  ACCUP: "treatment",
  DENT: "treatment",
  NURSE: "treatment",
  ANES: "treatment",
  DRUG: "drug",
  SUPPLY: "supply",
  EQUIP: "supply",
  PACKAGE: "package",
  MEH_PACKAGE: "package",
};

export const categoryOf = (item: InvoiceItem): BillingCategory =>
  P_TYPE_CATEGORY[item.p_type_code ?? ""] ?? "misc";

export const categoryCountsOf = (items: InvoiceItem[]) => {
  const counts: Partial<Record<BillingCategory, number>> = {};

  for (const item of items) {
    const category = categoryOf(item);
    counts[category] = (counts[category] ?? 0) + 1;
  }

  return CATEGORY_ORDER.filter((category) => counts[category]).map((category) => ({
    category,
    count: counts[category] ?? 0,
  }));
};

// ---------- ตัวกรอง ----------

export type PayFilter = "all" | PayStatus;

export type BillingFilter = {
  useDate: boolean;
  startDate: string;
  endDate: string;
  useEncounter: boolean;
  encounter: string;
  pay: PayFilter;
  q: string;
  category: BillingCategory | "all";
};

export const PAY_FILTER_OPTIONS: { value: PayFilter; label: string }[] = [
  { value: "all", label: "ทั้งหมด" },
  { value: "pending", label: "รอรับชำระ" },
  { value: "package", label: "ใช้สิทธิ์แพ็กเกจ" },
  { value: "paid", label: "ชำระแล้ว" },
];

export const emptyFilter = (today: string): BillingFilter => ({
  useDate: false,
  startDate: today,
  endDate: today,
  useEncounter: false,
  encounter: "",
  pay: "all",
  q: "",
  category: "all",
});

/** กรองทุกเงื่อนไขยกเว้นหมวด (ใช้นับจำนวนบน chip) */
export const filterExceptCategory = (items: InvoiceItem[], filter: BillingFilter) => {
  const q = filter.q.trim().toLowerCase();
  const start = beToAd(filter.startDate);
  const end = beToAd(filter.endDate);

  return items.filter((item) => {
    const created = moment(item.created);
    const inDate =
      (!start?.isValid() || created.isSameOrAfter(start, "day")) &&
      (!end?.isValid() || created.isSameOrBefore(end, "day"));

    return (
      (!filter.useDate || inDate) &&
      (!filter.useEncounter || !filter.encounter || item.encounter_number === filter.encounter) &&
      (filter.pay === "all" || payStatusOf(item) === filter.pay) &&
      (!q || `${item.code} ${item.product}`.toLowerCase().includes(q))
    );
  });
};

export const filterItems = (items: InvoiceItem[], filter: BillingFilter) =>
  filterExceptCategory(items, filter).filter(
    (item) => filter.category === "all" || categoryOf(item) === filter.category,
  );

// ---------- pop-up Package ----------

/** แถวของ BIL/invoiceitem/{pk}/package-apply/ */
export type PackageApplyRow = {
  package_order_item_id: number;
  package_code: string;
  package_name: string;
  product_code: string;
  product_name: string;
  quantity_total: string;
  /** คงเหลือก่อนใช้ (นับรวมที่รายการนี้ตัดใช้อยู่) */
  quantity_left: string;
  quantity_self_applied: string;
};

export type PackageDraft = Record<number, string>;

export const packageDraftOf = (rows: PackageApplyRow[]): PackageDraft => {
  const draft: PackageDraft = {};

  for (const row of rows) {
    draft[row.package_order_item_id] = String(Number(row.quantity_self_applied) || 0);
  }

  return draft;
};

export const packageSumOf = (draft: PackageDraft) => {
  let sum = 0;

  for (const value of Object.values(draft)) {
    sum += Number(value) || 0;
  }

  return sum;
};

export const packageErrorOf = (rows: PackageApplyRow[], draft: PackageDraft, quantity: number) => {
  for (const row of rows) {
    const value = Number(draft[row.package_order_item_id]) || 0;

    if (value < 0 || value > (Number(row.quantity_left) || 0)) {
      return `จำนวนที่นำมาใช้ต้องไม่ติดลบ และไม่เกินคงเหลือของ ${row.package_name}`;
    }
  }

  if (packageSumOf(draft) > quantity) {
    return `นำมาใช้รวมได้ไม่เกินจำนวนรายการ (${quantity})`;
  }

  return "";
};

export const packageApplyDataOf = (rows: PackageApplyRow[], draft: PackageDraft) => ({
  items: rows.map((row) => ({
    package_order_item_id: row.package_order_item_id,
    quantity_self_applied: Number(draft[row.package_order_item_id]) || 0,
  })),
});

export const packageFlashOf = (item: InvoiceItem, rows: PackageApplyRow[], draft: PackageDraft) => {
  const used = rows.filter((row) => (Number(draft[row.package_order_item_id]) || 0) > 0);

  if (used.length === 0) {
    return `${item.code} ไม่ใช้ Package · คิดเงินตามราคาปกติ`;
  }

  const names = used
    .map((row) => `${row.package_name} ×${Number(draft[row.package_order_item_id]) || 0}`)
    .join(", ");

  return `${item.code} ตัดใช้ Package: ${names}`;
};
